import _extends from '@babel/runtime/helpers/esm/extends';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo } from 'react';
import { useIntl, IntlProvider as IntlProvider$1 } from 'react-intl';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _inherits from '@babel/runtime/helpers/esm/inherits';
import _possibleConstructorReturn from '@babel/runtime/helpers/esm/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/esm/getPrototypeOf';
import _wrapNativeSuper from '@babel/runtime/helpers/esm/wrapNativeSuper';

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function () { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

/**
 * Error thrown when no locale is provided to IntlProvider or any parent internationalization providers.
 */
var MissingLocaleError = /*#__PURE__*/function (_Error) {
  _inherits(MissingLocaleError, _Error);

  var _super = _createSuper(MissingLocaleError);

  function MissingLocaleError() {
    _classCallCheck(this, MissingLocaleError);

    return _super.call(this, "[@quarry/intl Error MISSING_LOCALE] Missing locale for IntlProvider. Please provide a `locale` prop. If your IntlProvider is the child of another internationalization provider, you may provide a `locale` prop to only the parent provider and it will be used automatically.");
  }

  return MissingLocaleError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Provides props and fallback behavior for a react-intl IntlProvider that's optionally nested inside another IntlProvider.
 * - Allows shareable components to internationalize themselves while applying settings like locale and messages from the parent context.
 * - Sets a default `defaultLocale` of 'en-US'.
 * - Lets locales with no country code fall back to BCP 47 language tags (ex: "xx-XX").
 *
 * The ideal use case for this hook is to provide props for an IntlProvider that wraps a shareable component meant for consumption in internationalized parent apps. It can also be used with an app's topmost IntlProvider for its defaultLocale and fallback features.
 *
 * In the future, this will also support communication with Adobe-Intl-React's I18nProvider and react-aria's I18nProvider as parent contexts.
 *
 * @param {IntlConfigWithAllMessages} intlConfig Props for the current internationalization context
 * @param {IntlConfigWithMessagesForLocale | IntlConfigWithAllMessages} parentIntlConfig Props for a parent internationalization context
 * @returns {ReactIntlProviderProps} Props for a react-intl `IntlProvider` component
 */

function useIntlProvider(intlConfig) {
  var parentIntlConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var defaultLocale = intlConfig.defaultLocale,
      locale = intlConfig.locale;
  var parentDefaultLocale = parentIntlConfig.defaultLocale,
      parentLocale = parentIntlConfig.locale;
  defaultLocale = defaultLocale || parentDefaultLocale || 'en-US';
  locale = locale || parentLocale;

  if (!locale) {
    throw new MissingLocaleError();
  }

  var messages = useMessages(intlConfig, parentIntlConfig);
  return {
    defaultLocale: defaultLocale,
    locale: locale,
    messages: messages
  };
}
/**
 * Return the messages to provide to a react-intl `IntlProvider`.
 *
 * 1. In the messages dictionary, convert "xx_XX" locale keys to "xx-XX"
 * 2. Choose the best available locale to return messages for
 * 3. Merge messages with messages from a parent internationalization context, if one exists
 *
 * @private
 * @param {IntlConfigWithAllMessages} intlConfig Props for the current internationalization context
 * @param {IntlConfigWithMessagesForLocale | IntlConfigWithAllMessages} parentIntlConfig Props for a parent internationalization context
 * @returns {Messages} Processed and merged messages for a react-intl `IntlProvider` component
 */

function useMessages(intlConfig) {
  var parentIntlConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var defaultLocale = intlConfig.defaultLocale,
      locale = intlConfig.locale,
      messages = intlConfig.messages;
  var parentDefaultLocale = parentIntlConfig.defaultLocale,
      parentLocale = parentIntlConfig.locale,
      parentMessages = parentIntlConfig.messages;
  var normalizedMessages = normalizeMessages(messages);
  var messagesOfLocale = getMessagesOfLocale(normalizedMessages, defaultLocale, locale, parentDefaultLocale, parentLocale);
  var mergedMessages = mergeMessagesOfLocaleWithParentMessages(parentLocale, messagesOfLocale, parentMessages);
  return mergedMessages;
}
/**
 * Converts hyphens to underscores and returns locale.
 *
 * @private
 * @param {string} locale Locale formatted as BCP 47 language tag, delimited by hyphen or underscore (ex: "xx-XX" or "xx_XX")
 * @returns {string} Locale with underscores replaced by hyphens (ex: "xx-XX")
 */


function normalizeLocale(locale) {
  return locale.replace(/_/g, '-');
}
/**
 * Normalize each locale key in a messages object.
 *
 * @private
 * @param {AllMessages} messages All messages
 * @returns {AllMessages} All messages with normalized locale keys
 */


function normalizeMessages(messages) {
  var normalizedMessages = {};

  for (var localeKey in messages) {
    normalizedMessages[normalizeLocale(localeKey)] = messages[localeKey];
  }

  return normalizedMessages;
}
/**
 * Type guard for the AllMessages type, to distinguish it from Messages.
 *
 * This crude implementation takes a messages object and a locale key, then
 * checks if the locale exists on the messages object. If it does, then we
 * assume that `messages` has locale string keys, so its type is AllMessages.
 *
 * @private
 * @param {Messages | AllMessages | undefined} messages Object of messages for an internationalization provider
 * @param {string} locale A locale key to search for in the messages object
 * @returns {messages is AllMessages}
 */


function isAllMessages(messages, locale) {
  return Boolean(messages) && messages[locale] !== undefined;
}
/**
 * Merge messages from `messages` prop and any parent messages, if they exist.
 *
 * @private
 * @param {string} parentLocale Parent provider's `locale` prop
 * @param {Messages} messagesOfLocale IntlProvider's messages for the best available locale
 * @param {Messages | AllMessages} parentMessages Parent provider's `messages` prop
 * @returns {Messages} Messages merged with parent messages
 */


function mergeMessagesOfLocaleWithParentMessages() {
  var parentLocale = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var messagesOfLocale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var parentMessages = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var parentMessagesOfParentLocale = isAllMessages(parentMessages, parentLocale) ? parentMessages[parentLocale] || {} : parentMessages;
  return _objectSpread$1(_objectSpread$1({}, parentMessagesOfParentLocale), messagesOfLocale);
}
/**
 * Return messages for the given locale.
 *
 * If any locale argument is missing a country code, it will be matched to the closest locale supported in `messages` that does include a country code.
 *
 * To determine the locale, the order of precedence is:
 * 1. locale
 * 2. parent locale
 * 3. default locale
 * 4. parent default locale
 * 5. "en-US"
 *
 * @private
 * @param {AllMessages} messages Messages with locale keys formatted as BCP 47 language tags (ex: "xx-XX")
 * @param {string | undefined} locale IntlProvider's `locale` prop
 * @param {string | undefined} defaultLocale IntlProvider's `defaultLocale` prop
 * @param {string | undefined} parentLocale Parent provider's `locale` prop
 * @param {string | undefined} parentDefaultLocale Parent provider's `defaultLocale` prop
 * @returns {Messages} Messages for the given locale
 */


function getMessagesOfLocale(messages, defaultLocale, locale, parentDefaultLocale, parentLocale) {
  var _map = [locale, parentLocale, defaultLocale, parentDefaultLocale].map(function (loc) {
    return getClosestLocaleWithCountryCode(messages, loc);
  }),
      _map2 = _slicedToArray(_map, 4),
      closestLocale = _map2[0],
      closestParentLocale = _map2[1],
      closestDefaultLocale = _map2[2],
      closestParentDefaultLocale = _map2[3];

  return messages[closestLocale] || messages[closestParentLocale] || messages[closestDefaultLocale] || messages[closestParentDefaultLocale] || messages['en-US'];
}
/**
 * If the locale is only a language code without a country code (ex: "ja"), search the supported locales and return the BCP 47 locale with that language code (ex: "ja-JP").
 *
 * @private
 * @param {AllMessages} messages Messages with locale keys formatted as BCP 47 language tags (ex: "xx-XX")
 * @param {string} locale Locale with language code and optional country code (ex: "xx" or "xx-XX")
 * @returns {string} The closest supported locale
 */


function getClosestLocaleWithCountryCode(messages, locale) {
  // In messages object, find the locale key that starts with the given locale prop.
  return Object.keys(messages).find(function (localeKey) {
    return locale && localeKey.startsWith(locale);
  }) || '';
}

/**
 * Returns internationalization props from a parent react-intl provider, if one exists.
 *
 * If none exists, returns an {@link IntlConfigWithMessagesForLocale|IntlConfigWithMessagesForLocale} object with empty fields.
 *
 * @private
 */

function useReactIntl() {
  try {
    var _useIntl = useIntl(),
        defaultLocale = _useIntl.defaultLocale,
        locale = _useIntl.locale,
        messages = _useIntl.messages;

    return {
      defaultLocale: defaultLocale,
      locale: locale,
      messages: messages
    };
  } catch (e) {
    /**
     * When react-intl's useIntl() is called and no parent react-intl provider exists,
     * react-intl throws an error prepended with the string "[React Intl]".
     */
    var hasNoReactIntlParent = e.message.includes('React Intl');

    if (hasNoReactIntlParent) {
      return {
        defaultLocale: undefined,
        locale: undefined,
        messages: undefined
      };
    } // Throw an unrelated error that occured.


    throw e;
  }
}
function useParentIntlProvider() {
  // TODO: handle other libs like Adobe-React-Intl, react-aria
  return useReactIntl();
}

/**
 * Wraps a react-intl IntlProvider and augments it with extra features and fallback behavior.
 *
 * Gets info from a parent internationalization context if it exists, then delegates all logic to `useIntlProvider`.
 */
var IntlProvider = function (_ref) {
  var children = _ref.children,
      defaultLocale = _ref.defaultLocale,
      locale = _ref.locale,
      messages = _ref.messages,
      props = _objectWithoutProperties(_ref, ["children", "defaultLocale", "locale", "messages"]);

  var intlConfig = {
    defaultLocale: defaultLocale,
    locale: locale,
    messages: messages
  };
  var parentIntlConfig = useParentIntlProvider();
  var intlProviderProps = useMemo(function () {
    return useIntlProvider(intlConfig, parentIntlConfig);
  }, [].concat(_toConsumableArray(Object.values(intlConfig)), _toConsumableArray(Object.values(parentIntlConfig))));
  return /*#__PURE__*/React.createElement(IntlProvider$1, _extends({}, intlProviderProps, props), children);
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var mergeDictionaries = function (dictionaries) {
  return dictionaries.reduce(function (aggregator, current) {
    for (var _i = 0, _Object$entries = Object.entries(current); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          _key = _Object$entries$_i[0],
          value = _Object$entries$_i[1];

      aggregator[_key] = _objectSpread(_objectSpread({}, aggregator[_key]), value);
    }

    return aggregator;
  }, {});
};

export { IntlProvider, mergeDictionaries, useIntlProvider };
